import { toast } from "react-toastify";
import { call, put, takeLatest } from "redux-saga/effects";
import * as actions from "./Actions";
import * as api from "./Api";
import Types from "./Types";

function unwrapList(result) {
  const raw = result?.data;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return raw?.data?.data ?? [];
}

function unwrapRow(result) {
  const raw = result?.data;
  return raw?.data ?? raw;
}

function* getOfficeHours() {
  try {
    yield put(actions.officeHoursLoading(true));
    const result = yield call(api.getOfficeHours);
    const list = unwrapList(result);
    yield put(actions.getOfficeHoursSuccess({ data: list }));
  } catch (error) {
    toast.error(
      error?.response?.data?.message || "Failed to load office hours"
    );
    yield put(actions.getOfficeHoursFail());
  } finally {
    yield put(actions.officeHoursLoading(false));
  }
}

function* createOfficeHour(action) {
  try {
    yield put(actions.officeHoursLoading(true));
    const result = yield call(api.createOfficeHour, action.payload);
    const row = unwrapRow(result);
    yield put(actions.createOfficeHourSuccess({ data: row }));
    toast.success(result?.data?.message || "Created successfully");
    if (typeof action.onDone === "function") {
      action.onDone();
    }
  } catch (error) {
    yield put(actions.createOfficeHourFail());
    toast.error(
      error?.response?.data?.message || "Failed to create office hour"
    );
  } finally {
    yield put(actions.officeHoursLoading(false));
  }
}

function* updateOfficeHour(action) {
  try {
    yield put(actions.officeHoursLoading(true));
    const { id, body } = action.payload;
    const result = yield call(api.updateOfficeHour, { id, body });
    const row = unwrapRow(result);
    if (row && row.id != null) {
      yield put(actions.updateOfficeHourSuccess({ data: row }));
      toast.success(result?.data?.message || "Updated successfully");
      if (typeof action.onDone === "function") {
        action.onDone();
      }
    } else {
      throw new Error("Invalid response");
    }
  } catch (error) {
    yield put(actions.updateOfficeHourFail());
    toast.error(
      error?.response?.data?.message || "Failed to update office hour"
    );
  } finally {
    yield put(actions.officeHoursLoading(false));
  }
}

function* deleteOfficeHour({ payload }) {
  try {
    yield put(actions.officeHoursLoading(true));
    yield call(api.deleteOfficeHour, payload);
    yield put(actions.deleteOfficeHourSuccess(payload));
    toast.success("Deleted successfully");
  } catch (error) {
    toast.error(
      error?.response?.data?.message || "Failed to delete office hour"
    );
    yield put(actions.deleteOfficeHourFail());
  } finally {
    yield put(actions.officeHoursLoading(false));
  }
}

function* cancelOfficeHour({ payload }) {
  try {
    yield put(actions.officeHoursLoading(true));
    const result = yield call(api.cancelOfficeHour, payload);
    const row = unwrapRow(result);
    yield put(
      actions.cancelOfficeHourSuccess(
        row && row.id != null ? { data: row } : { data: { id: payload, student_id: null, student: null } }
      )
    );
    toast.success(result?.data?.message || "Booking revoked successfully");
  } catch (error) {
    toast.error(
      error?.response?.data?.message || "Failed to revoke office hour booking"
    );
    yield put(actions.cancelOfficeHourFail());
  } finally {
    yield put(actions.officeHoursLoading(false));
  }
}

export default function* officeHoursSaga() {
  yield takeLatest(Types.GET_OFFICE_HOURS_REQUEST, getOfficeHours);
  yield takeLatest(Types.CREATE_OFFICE_HOUR_REQUEST, createOfficeHour);
  yield takeLatest(Types.UPDATE_OFFICE_HOUR_REQUEST, updateOfficeHour);
  yield takeLatest(Types.DELETE_OFFICE_HOUR_REQUEST, deleteOfficeHour);
  yield takeLatest(Types.CANCEL_OFFICE_HOUR_REQUEST, cancelOfficeHour);
}
